import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { cfetch } from '@confluence/network';
import {
	superbatchCSSRecorder,
	superbatchJSRecorder,
} from '@confluence/ssr-utilities/entry-points/SuperbatchResourceRecorder';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient';
import { stringifyForSSR } from '@confluence/ssr-utilities/entry-points/stringifyForSSR';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError';
import { WebPanelLocationQuery } from '@confluence/web-panel-location/entry-points/WebPanelLocationQuery';
import type {
	WebPanelLocationQueryType,
	WebPanelLocationQueryVariablesType,
} from '@confluence/web-panel-location/entry-points/WebPanelLocationQueryTypes';
import {
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations';

// Track the connect resources promise so it can be awaited before rendering head
let connectWebResourcesPromise: Promise<void> | null = null;

export const getConnectWebResourcesPromise = () => connectWebResourcesPromise;

// For testing: reset the promise state
export const __resetConnectWebResourcesPromise = () => {
	connectWebResourcesPromise = null;
};

function convertResourceToScriptTags(resource: {
	url: string;
	resourceType: 'CSS' | 'JAVASCRIPT';
	ieOnly: boolean;
	key: string;
	batchType: string;
}) {
	const nonce = window.__SSR_NONCE_VALUE__;
	if (resource.resourceType === 'CSS') {
		return `<link rel="stylesheet" nonce="${nonce}" href="${resource.url}" data-key="${resource.key}" data-batch-type="${resource.batchType}"${resource.ieOnly ? ' data-ie-only="true"' : ''} />`;
	} else if (resource.resourceType === 'JAVASCRIPT') {
		return `<script nonce="${nonce}" src="${resource.url}" data-key="${resource.key}" data-batch-type="${resource.batchType}"${resource.ieOnly ? ' data-ie-only="true"' : ''}></script>`;
	}
	return '';
}

export const preloadConnectWebResources = (contentId?: string): Promise<void> => {
	const promise = (async () => {
		if (
			!(
				fg('content_early_connect_iframe_initialization') ||
				expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false)
			)
		) {
			return;
		}

		// When early webpanel initialization is enabled, check if there are any webpanels
		// before preloading connect resources
		if (contentId) {
			// Query both ATL_GENERAL and ATL_FOOTER locations for webpanels
			let generalPanelsData: WebPanelLocationQueryType | null | undefined;
			let footerPanelsData: WebPanelLocationQueryType | null | undefined;

			const generalPanelsPromise = query<
				WebPanelLocationQueryType,
				WebPanelLocationQueryVariablesType
			>({
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				query: WebPanelLocationQuery,
				variables: {
					contentId,
					location: ATL_GENERAL,
				},
			}).then((result) => {
				generalPanelsData = result.data;
			});

			const footerPanelsPromise = query<
				WebPanelLocationQueryType,
				WebPanelLocationQueryVariablesType
			>({
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				query: WebPanelLocationQuery,
				variables: {
					contentId,
					location: ATL_FOOTER,
				},
			}).then((result) => {
				footerPanelsData = result.data;
			});

			// Wait for both queries, but don't fail fast if one errors
			const errors = await waitForAllReturnWhenError([generalPanelsPromise, footerPanelsPromise]);

			if (errors) {
				// If webpanel queries failed, continue with preloading to avoid blocking
				ssrPrepareErrorsClient.submitError(
					new Error(
						`Failed to query webpanels before preloading connect resources: ${errors.map((e) => e.message).join(', ')}`,
					),
				);
			} else {
				// If no webpanels exist in either location, no need to preload connect resources
				const hasGeneralPanels = (generalPanelsData?.webPanels?.length ?? 0) > 0;
				const hasFooterPanels = (footerPanelsData?.webPanels?.length ?? 0) > 0;

				if (!hasGeneralPanels && !hasFooterPanels) {
					return;
				}
			}
		}

		try {
			const response = await cfetch('/wiki/rest/webResources/1.0/resources', {
				method: 'POST',
				body: JSON.stringify({
					r: [
						'confluence.web.resources:querystring',
						'confluence.web.resources:navigator-context',
						'com.atlassian.plugins.atlassian-connect-plugin:confluence-atlassian-connect-resources-v5',
						'confluence.web.resources:view-content',
						'confluence.macros.advanced:get-more',
						'com.atlassian.confluence.plugins.confluence-view-file-macro:view-file-macro-resources',
					],
					c: [],
					xc: ['_super'],
					xr: [
						'com.atlassian.plugins.jquery:jquery',
						'com.atlassian.plugins.jquery:jquery-migrate',
					],
				}),
			});

			if (!response.ok) {
				throw new Error(`Failed to preload web resources: ${response.status}`);
			}

			const data = await response.json();

			// unparsedData in the response will later be claimed by window.WRM.data.claim()
			// This need to be fore all the JS scripts because they might claim the data while executing.
			if (data?.unparsedData) {
				const entries = [] as string[];
				for (const [key, value] of Object.entries(data.unparsedData)) {
					// value is a stringified JSON object.
					entries.push(`d["${key}"]=${stringifyForSSR(value)}`);
				}
				superbatchJSRecorder.addConnectResource(
					`<script>(function(d){if(d){${entries.join(';')}}})(window?.WRM?._unparsedData)</script>`,
				);
			}

			superbatchJSRecorder.addConnectResource(
				data?.resources
					?.filter((resource) => resource.resourceType === 'JAVASCRIPT')
					?.map((resource) => convertResourceToScriptTags(resource))
					.join('\n'),
			);

			superbatchCSSRecorder.addConnectResource(
				data?.resources
					?.filter((resource) => resource.resourceType === 'CSS')
					?.map((resource) => convertResourceToScriptTags(resource))
					.join('\n'),
			);
		} catch (error) {
			ssrPrepareErrorsClient.submitError(
				new Error(`Unable to preload connect web resources: ${error.message}`),
			);
		}
	})();

	connectWebResourcesPromise = promise;
	return promise;
};
